import React, { useState } from 'react';
import { 
  Send, 
  Copy, 
  Check, 
  Linkedin, 
  Mail, 
  Sparkles, 
  Zap, 
  UserCheck, 
  Compass,
  Building,
  Target
} from 'lucide-react';

interface OutreachGeneratorProps {
  initialData?: {
    company: string;
    role: string;
    emailSubject: string;
    emailBody: string;
    inMail: string;
  };
}

export const OutreachGenerator: React.FC<OutreachGeneratorProps> = ({ initialData }) => {
  const [recipientName, setRecipientName] = useState('David');
  const [recipientRole, setRecipientRole] = useState('Head of Engineering / Technical Director');
  const [companyName, setCompanyName] = useState(initialData?.company || 'FinTech Scaleup UK');
  const [targetRole, setTargetRole] = useState(initialData?.role || 'Senior Software Engineer');
  const [techStack, setTechStack] = useState('React, TypeScript, Node.js, Cloudflare');
  const [selectedAngle, setSelectedAngle] = useState<'overflow' | 'value' | 'speed'>('overflow');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  // Generate Email Subject & Body based on chosen angle
  const getEmailContent = () => {
    if (selectedAngle === 'overflow') {
      return {
        subject: `Quick question re: ${companyName}'s engineering sprint capacity & overflow`,
        body: `Hi ${recipientName},\n\nI noticed you're leading the engineering team at ${companyName} and recently opened a search for a ${targetRole}.\n\nRather than dropping a standard CV into the recruiter queue, I wanted to reach out directly: I'm a UK-based software engineer specializing in ${techStack}.\n\nWhether you need permanent headcount or immediate contract/overflow dev support to clear your sprint backlog with zero onboarding friction, I'm available to hit the ground running this week.\n\nHere is my GitHub & project portfolio: [Your Portfolio Link]\n\nWould you be open to a 5-minute introductory call this week?\n\nBest regards,\n[Your Name]\n[Your Phone] | [LinkedIn Profile]`
      };
    } else if (selectedAngle === 'value') {
      return {
        subject: `${techStack.split(',')[0]} developer for ${companyName}`,
        body: `Hi ${recipientName},\n\nI saw that ${companyName} is building with ${techStack} and scaling the engineering team.\n\nI'm a senior software engineer with hands-on experience delivering resilient web applications, optimizing API latencies, and shipping reliable code on tight release cycles.\n\nI'd love to learn more about the technical challenges your team is currently solving and explore how I can contribute immediately.\n\nAre you available for a brief 5-minute chat this Thursday or Friday?\n\nBest regards,\n[Your Name]\n[Your Phone] | [GitHub/Portfolio]`
      };
    } else {
      return {
        subject: `48-hour performance & speed fix for ${companyName}`,
        body: `Hi ${recipientName},\n\nI took a quick look at ${companyName}'s web application and noticed some quick optimization opportunities that could improve page load speed and Core Web Vitals.\n\nI specialize in web performance, API latency reduction, and edge caching (Cloudflare/CDN).\n\nIf you have any pesky performance bottlenecks or bug tickets taking up your team's sprint time, I'd be happy to tackle a small test ticket to demonstrate my speed and code quality.\n\nOpen to connecting?\n\nBest,\n[Your Name]\n[Your Phone]`
      };
    }
  };

  const getLinkedInInMail = () => {
    if (selectedAngle === 'overflow') {
      return `Hi ${recipientName}, saw you're scaling engineering at ${companyName} for ${targetRole} roles. I'm a UK engineer (${techStack.split(',').slice(0, 2).join(' & ')}) available for immediate start (contract or perm) with zero onboarding lag. Would love to connect!`;
    } else if (selectedAngle === 'value') {
      return `Hi ${recipientName}, love what ${companyName} is building with ${techStack.split(',')[0]}. I'm an experienced UK developer looking to contribute to your current sprint roadmap. Would be great to connect and share recent project samples.`;
    } else {
      return `Hi ${recipientName}, reaching out directly regarding ${companyName}'s tech stack. I provide rapid overflow development and speed optimizations. Open to connecting in case your team ever needs an extra pair of hands on short notice!`;
    }
  };

  const emailData = getEmailContent();
  const linkedInData = getLinkedInInMail();

  return (
    <div className="space-y-8 pb-16">
      
      {/* Header */}
      <div className="border-b border-slate-800/80 pb-6">
        <div className="inline-flex items-center space-x-2 px-2.5 py-0.5 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-400 text-xs font-semibold">
          <Send className="w-3.5 h-3.5" />
          <span>Bypass the Recruiter Black Hole</span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-white mt-1">Hiring Manager Direct Outreach Generator</h1>
        <p className="text-xs sm:text-sm text-slate-400">
          Reaching Technical Directors and CTOs directly yields a <strong>5x to 10x higher response rate</strong> than blind job portal applications.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Left Form: Target Parameters */}
        <div className="lg:col-span-5 rounded-2xl bg-slate-900 border border-slate-800 p-6 space-y-4 shadow-lg">
          <h3 className="text-sm font-bold text-white flex items-center space-x-2">
            <Target className="w-4 h-4 text-sky-400" />
            <span>Target Prospect Details</span>
          </h3>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-slate-400">Hiring Lead First Name</label>
              <input
                type="text"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder="e.g. David"
                className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-400">Company Name</label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="e.g. FinTech Scaleup"
                className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white"
              />
            </div>
          </div>

          <div>
            <label className="text-[11px] font-semibold text-slate-400">Their Role / Title</label>
            <input
              type="text"
              value={recipientRole}
              onChange={(e) => setRecipientRole(e.target.value)}
              placeholder="e.g. CTO / Head of Engineering"
              className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white"
            />
          </div>

          <div>
            <label className="text-[11px] font-semibold text-slate-400">Relevant Tech Stack</label>
            <input
              type="text"
              value={techStack}
              onChange={(e) => setTechStack(e.target.value)}
              placeholder="e.g. React, TypeScript, Node.js"
              className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-sky-300 font-mono"
            />
          </div>

          {/* Angle Selector */}
          <div className="space-y-2 pt-2 border-t border-slate-800">
            <label className="text-[11px] font-semibold text-slate-400">Select Pitch Angle</label>
            
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setSelectedAngle('overflow')}
                className={`w-full text-left p-3 rounded-xl border text-xs transition-all ${
                  selectedAngle === 'overflow'
                    ? 'bg-sky-500/20 border-sky-500 text-sky-200'
                    : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-slate-200'
                }`}
              >
                <div className="font-bold flex items-center space-x-1.5">
                  <Zap className="w-3.5 h-3.5 text-amber-400" />
                  <span>1. Agency Overflow & Sprint Backlog (Fastest)</span>
                </div>
                <div className="text-[10px] text-slate-400 mt-1">Offers immediate hands-on help without onboarding lag.</div>
              </button>

              <button
                type="button"
                onClick={() => setSelectedAngle('value')}
                className={`w-full text-left p-3 rounded-xl border text-xs transition-all ${
                  selectedAngle === 'value'
                    ? 'bg-sky-500/20 border-sky-500 text-sky-200'
                    : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-slate-200'
                }`}
              >
                <div className="font-bold flex items-center space-x-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-sky-400" />
                  <span>2. Value-First Technical Matching</span>
                </div>
                <div className="text-[10px] text-slate-400 mt-1">Highlights matching stack and problem-solving track record.</div>
              </button>

              <button
                type="button"
                onClick={() => setSelectedAngle('speed')}
                className={`w-full text-left p-3 rounded-xl border text-xs transition-all ${
                  selectedAngle === 'speed'
                    ? 'bg-sky-500/20 border-sky-500 text-sky-200'
                    : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-slate-200'
                }`}
              >
                <div className="font-bold flex items-center space-x-1.5">
                  <Compass className="w-3.5 h-3.5 text-emerald-400" />
                  <span>3. Risk-Free Trial Ticket / Speed Fix</span>
                </div>
                <div className="text-[10px] text-slate-400 mt-1">Offers to solve an immediate pain point or slow page load.</div>
              </button>
            </div>
          </div>

          {/* Quick Pro-Tip Box */}
          <div className="p-3.5 rounded-xl bg-slate-950/80 border border-slate-800/80 text-[11px] text-slate-400 space-y-1">
            <div className="font-bold text-slate-300">💡 How to find UK Hiring Leads:</div>
            <div>1. On LinkedIn, search: <code>"{companyName}" + "Head of Engineering" OR "Technical Director" OR "CTO"</code></div>
            <div>2. Find work email patterns via tools like Hunter.io or Anymail Finder (usually <code>firstname@company.co.uk</code>).</div>
          </div>

        </div>

        {/* Right Output: Ready-to-Send Email & InMail */}
        <div className="lg:col-span-7 space-y-6">
          
          {/* Email Template Card */}
          <div className="rounded-2xl bg-slate-900 border border-slate-800 p-6 space-y-4 shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center space-x-2 text-xs font-bold text-white">
                <Mail className="w-4 h-4 text-sky-400" />
                <span>Cold Email Pitch (100-Word Rule)</span>
              </div>

              <button
                onClick={() => handleCopy(`Subject: ${emailData.subject}\n\n${emailData.body}`, 'email')}
                className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-sky-500 hover:bg-sky-400 text-white shadow-sm transition-all"
              >
                {copiedKey === 'email' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedKey === 'email' ? 'Copied Full Email!' : 'Copy Subject & Body'}</span>
              </button>
            </div>

            {/* Subject Line */}
            <div className="space-y-1">
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Subject Line</span>
              <div className="bg-slate-950 p-3 rounded-xl border border-slate-800 text-xs font-semibold text-sky-300 flex items-center justify-between">
                <span>{emailData.subject}</span>
                <button
                  onClick={() => handleCopy(emailData.subject, 'subj')}
                  className="text-slate-500 hover:text-white text-[10px] pl-2"
                >
                  {copiedKey === 'subj' ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>

            {/* Email Body */}
            <div className="space-y-1">
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Email Body</span>
              <div className="bg-slate-950 p-4 rounded-xl border border-slate-800 text-xs text-slate-200 leading-relaxed whitespace-pre-wrap font-sans">
                {emailData.body}
              </div>
            </div>
          </div>

          {/* LinkedIn InMail Card */}
          <div className="rounded-2xl bg-slate-900 border border-slate-800 p-6 space-y-4 shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center space-x-2 text-xs font-bold text-white">
                <Linkedin className="w-4 h-4 text-sky-400" />
                <span>LinkedIn Direct InMail / Connection Message (&lt;300 chars)</span>
              </div>

              <button
                onClick={() => handleCopy(linkedInData, 'inmail')}
                className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-slate-200 transition-all"
              >
                {copiedKey === 'inmail' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedKey === 'inmail' ? 'Copied!' : 'Copy InMail'}</span>
              </button>
            </div>

            <div className="bg-slate-950 p-4 rounded-xl border border-slate-800 text-xs text-slate-200 leading-relaxed whitespace-pre-wrap">
              {linkedInData}
            </div>

            <div className="flex items-center justify-between text-[11px] text-slate-400">
              <span>Characters: {linkedInData.length} / 300 limit</span>
              <span className="text-emerald-400">✓ Passes connection request limit</span>
            </div>
          </div>

        </div>

      </div>

    </div>
  );
};
